import { Module, MutationTree, ActionTree, GetterTree } from 'vuex'
import { RootState } from '@/types/store/index'
import { VideoState } from '@/types/store/video'
import { VideoAttributes } from '@/types/models/video'
import { VideoCategoryAttributes } from '@/types/models/video-category'
import { VideoWatchHistoryAttributes } from '@/types/models/video-watch-history'
import { axios, ApiResponse } from '@/plugins/axios'
import { toArray } from '@/utils/array'
import { getStartDateQuery, getEndDateQuery } from '@/types/forms/date-range-picker'
import { getSortQuery } from '@/types/forms/sort-switch'
import { isWithinStartDate, isWithinEndDate } from '@/plugins/dayjs'
import { sortBy } from 'lodash'
import { toSlice } from '@/utils/slice'

const state = (): VideoState => ({
  videos: undefined,
  videoDetails: undefined,
  videoCategories: undefined,
  videoWatchHistories: undefined,
})

const getters: GetterTree<VideoState, RootState> = {
  sortVideosByQuery: () => (videos: VideoAttributes[]): VideoAttributes[] => {
    return getSortQuery() ? sortBy(videos, 'published_at').reverse() : sortBy(videos, 'published_at')
  },
  filterVideosBySearchWord: (state) => (search_word: string): VideoAttributes[] => {
    let result = [...toArray(state.videos)]
    const slicedSearchWords = toSlice(search_word)

    slicedSearchWords.forEach((slicedSearchWord) => { 
      const searched_titles = result.filter((item: VideoAttributes) => {
        return item.title?.match(slicedSearchWord)
      })
      const searched_tags = result.filter((item: VideoAttributes) => {
        return toArray(item.tags).find(tag => tag.name?.match(slicedSearchWord))
      }) 
      const searched_bodys = result.filter((item: VideoAttributes) => {
        return item.body?.match(slicedSearchWord)
      })
      result = Array.from(new Set([...searched_titles, ...searched_bodys, ...searched_tags]))
    })
    return result
  },
  filterVideosByTagIds: (state) => (tagIds: number[] | string[]): VideoAttributes[] => {
    let result = [...toArray(state.videos)]
    tagIds.forEach(tag_id => {
      result = result.filter((item: VideoAttributes) => {
        const tag = toArray(item.tags).find(tag => tag.id === Number(tag_id))
        return !!tag
      })
    })
    return result
  },
  filterVideosByDate: () => (videos: VideoAttributes[]): VideoAttributes[] => {
    const strStartDate = getStartDateQuery()
    const strEndDate = getEndDateQuery()
    return videos.filter(video => {
      const withinEndDate = isWithinEndDate(video.published_at, strEndDate)
      const withinStartDate = isWithinStartDate(video.published_at, strStartDate)
      return withinStartDate && withinEndDate
    })
  },
  filterWatchHistoriesByDate: (state) => (): VideoWatchHistoryAttributes[] => {
    const strStartDate = getStartDateQuery()
    const strEndDate = getEndDateQuery()
    return toArray(state.videoWatchHistories).filter(watchHistory => {
      const withinEndDate = isWithinEndDate(watchHistory.created_at, strEndDate)
      const withinStartDate = isWithinStartDate(watchHistory.created_at, strStartDate)
      return withinStartDate && withinEndDate
    })
  },
  formatVideoCategories: (state, getters) => (): VideoCategoryAttributes[] => {
    return toArray(state.videoCategories).map(category => {
      const tagIds = toArray(category.tags).flatMap(tag => tag.id)
      const videos = tagIds.length ? getters.filterVideosByTagIds(tagIds) as VideoAttributes[] : []
      category.videos = videos
      return category
    })
  },
  filterVideoCategoriesById: (state, getters) => (video_id: number | string): VideoCategoryAttributes[] => {
    const videoCategories = getters.formatVideoCategories() as VideoCategoryAttributes[]
    return videoCategories.filter(category => {
      return toArray(category.videos).some(video => video?.id == video_id)
    })
  },
  getLatestVideos: (state) => (): VideoAttributes[] => {
    return toArray(state.videos).slice(0, 5)
  },
  getLatestWatchHistories: (state) => (): VideoWatchHistoryAttributes[] => {
    return toArray(state.videoWatchHistories).slice(0, 4)
  },
  getLatestVideoCategories: (state, getters) => (): VideoCategoryAttributes[] => {
    const videoCategories = getters.formatVideoCategories() as VideoCategoryAttributes[]
    return videoCategories.slice(0, 3)
  },
}

const mutations: MutationTree<VideoState> = {
  setVideos (state, videos?: VideoAttributes[]) {
    state.videos = videos
  },
  setVideoDetails (state, videoDetails?: VideoAttributes) {
    state.videoDetails = videoDetails
  },
  setVideoCategories (state, videoCategories?: VideoCategoryAttributes[]) {
    state.videoCategories = videoCategories
  },
  setVideoWatchHistories (state, videoWatchHistories?: VideoWatchHistoryAttributes[]) {
    state.videoWatchHistories = videoWatchHistories
  }
}

const actions: ActionTree<VideoState, RootState> = {
  fetchVideos: async ({ commit }) => {
    const response = await axios.get<ApiResponse<VideoAttributes[]>>('/video')
    commit('setVideos', response.data.payload)
  },
  fetchVideoCategories: async ({ commit }) => {
    const response = await axios.get<ApiResponse<VideoCategoryAttributes[]>>('/video/category')
    commit('setVideoCategories', response.data.payload)
  },
  fetchVideoWatchHistories: async ({ commit }) => {
    const response = await axios.get<ApiResponse<VideoWatchHistoryAttributes[]>>('/video/watch-history')
    commit('setVideoWatchHistories', response.data.payload)
  }
}

export const video: Module<VideoState, RootState> = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
